import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, Upload, X, Loader2, Globe, RefreshCw, Cloud, Package, DollarSign } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
    name: "", prefix: "", website: "", notes: "",
    csv_url: "", csv_idcat: "1",
    cron_full_enabled: true, cron_full_hour: 3,
    cron_stock_enabled: true, cron_stock_interval_hours: 3,
    delimiter: ";",
    default_markup_pct: 30,
    use_street_price: true,
    pricing_mode: "street",
    pricing_value: 0,
    strip_code_prefix: true,
    translate_color: true,
    generate_pt_description: true,
    column_map: {
        macro_cat: "MacroCat", cat: "Cat", code: "Code", description: "Description",
        brand: "Brand", color: "Color", price_cost: "Price", stock: "Stock",
        barcode: "Barcode", weight: "Weight", street_price: "Street_price",
        vat: "Vat", url_photo: "URL_photo", description_html: "Description_HTML",
    },
    brand_codes: {
        HP: "HP", BROTHER: "BR", CANON: "CA", EPSON: "EP", XEROX: "XE",
        SAMSUNG: "SA", LEXMARK: "LE", RICOH: "RI", KYOCERA: "KY", OKI: "OK",
    },
};

const PRICING_MODES = [
    { v: "street", l: "PVR (Street price tal e qual)", needs: false },
    { v: "cost_markup", l: "Custo + Markup %", needs: "%" },
    { v: "street_plus_pct", l: "PVR + %", needs: "%" },
    { v: "street_plus_eur", l: "PVR + valor fixo €", needs: "€" },
    { v: "manual", l: "Preço manual fixo €", needs: "€" },
];

export default function AdminSuppliers() {
    const [list, setList] = useState([]);
    const [edit, setEdit] = useState(null);
    const [importing, setImporting] = useState(null);
    const [syncing, setSyncing] = useState({});

    const load = () => api.get("/admin/suppliers").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        try {
            const payload = { ...data, default_markup_pct: Number(data.default_markup_pct) };
            if (data.id) await api.put(`/admin/suppliers/${data.id}`, payload);
            else await api.post("/admin/suppliers", payload);
            toast.success("Fornecedor guardado");
            setEdit(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        }
    };
    const del = async (id) => {
        if (!window.confirm("Eliminar fornecedor?")) return;
        await api.delete(`/admin/suppliers/${id}`);
        load();
    };

    const runSync = async (sup, mode) => {
        if (!sup.csv_url) {
            toast.error("Configure primeiro o URL CSV remoto.");
            return;
        }
        const labels = { full: "Importação completa", prices: "Actualização de preços", stock: "Actualização de stocks" };
        setSyncing((s) => ({ ...s, [sup.id]: mode }));
        toast.info(`${labels[mode]} a iniciar… pode demorar até 1min`);
        try {
            const params = new URLSearchParams({ mode, only_in_stock: "false" });
            const { data } = await api.post(
                `/admin/suppliers/${sup.id}/sync-from-url?${params.toString()}`,
                null,
                { timeout: 600000 },
            );
            toast.success(
                `${labels[mode]}: ${data.created} criados, ${data.updated} actualizados, ` +
                `${data.skipped_disabled || 0} desactivados ignorados, ${data.skipped_manual_price || 0} preços manuais preservados`,
                { duration: 8000 },
            );
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro no sync remoto");
        } finally {
            setSyncing((s) => ({ ...s, [sup.id]: null }));
        }
    };

    const fmtTs = (iso) => iso ? new Date(iso).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "—";

    return (
        <div data-testid="admin-suppliers-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h1 className="font-display font-black text-4xl tracking-tighter">Fornecedores</h1>
                <button onClick={() => setEdit(EMPTY)} className="btn-primary" data-testid="new-supplier-btn">
                    <Plus size={16} /> Novo fornecedor
                </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {list.map((s) => (
                    <div key={s.id} className="border-2 border-ink bg-white p-5" data-testid={`supplier-${s.prefix}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-2xl font-black text-magenta">{s.prefix}</span>
                                    <h2 className="font-display font-black text-xl">{s.name}</h2>
                                </div>
                                {s.website && (
                                    <a href={s.website} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-ink/60 hover:text-magenta inline-flex items-center gap-1 mt-1">
                                        <Globe size={11} /> {s.website}
                                    </a>
                                )}
                                {s.notes && <p className="text-xs text-ink/70 mt-2">{s.notes}</p>}
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => setEdit(s)} className="p-2 hover:text-magenta">
                                    <Pencil size={14} />
                                </button>
                                <button onClick={() => del(s.id)} className="p-2 hover:text-destructive">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-3 gap-2 border-t-2 border-ink pt-3 text-xs font-mono">
                            <div>
                                <div className="label-mono text-ink/50">Markup</div>
                                <div className="font-bold mt-1">{s.default_markup_pct}%</div>
                            </div>
                            <div>
                                <div className="label-mono text-ink/50">Preço</div>
                                <div className="font-bold mt-1">{s.use_street_price ? "Street" : "Custo+Markup"}</div>
                            </div>
                            <div>
                                <div className="label-mono text-ink/50">Marcas</div>
                                <div className="font-bold mt-1">{Object.keys(s.brand_codes || {}).length}</div>
                            </div>
                        </div>

                        {s.csv_url ? (
                            <div className="mt-4 border-t-2 border-ink pt-3" data-testid={`sync-section-${s.prefix}`}>
                                <div className="label-mono text-magenta mb-2 inline-flex items-center gap-1">
                                    <Cloud size={11} /> Sync remoto activo
                                </div>
                                <div className="text-[11px] font-mono text-ink/60 mb-3 break-all line-clamp-1" title={s.csv_url}>
                                    {s.csv_url.replace("{idcat}", s.csv_idcat || "1")}
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-[10px] font-mono mb-3">
                                    <div>
                                        <div className="text-ink/40">Último completo</div>
                                        <div>{fmtTs(s.last_sync_full_at)}</div>
                                    </div>
                                    <div>
                                        <div className="text-ink/40">Últ. preços</div>
                                        <div>{fmtTs(s.last_sync_prices_at)}</div>
                                    </div>
                                    <div>
                                        <div className="text-ink/40">Últ. stock</div>
                                        <div>{fmtTs(s.last_sync_stock_at)}</div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <button
                                        onClick={() => runSync(s, "full")}
                                        disabled={!!syncing[s.id]}
                                        className="border-2 border-ink py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-magenta hover:text-white hover:border-magenta disabled:opacity-50 inline-flex items-center justify-center gap-1"
                                        data-testid={`sync-full-${s.prefix}`}
                                    >
                                        {syncing[s.id] === "full" ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
                                        Completo
                                    </button>
                                    <button
                                        onClick={() => runSync(s, "prices")}
                                        disabled={!!syncing[s.id]}
                                        className="border-2 border-ink py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-magenta hover:text-white hover:border-magenta disabled:opacity-50 inline-flex items-center justify-center gap-1"
                                        data-testid={`sync-prices-${s.prefix}`}
                                    >
                                        {syncing[s.id] === "prices" ? <Loader2 className="animate-spin" size={12} /> : <DollarSign size={12} />}
                                        Preços
                                    </button>
                                    <button
                                        onClick={() => runSync(s, "stock")}
                                        disabled={!!syncing[s.id]}
                                        className="border-2 border-ink py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-magenta hover:text-white hover:border-magenta disabled:opacity-50 inline-flex items-center justify-center gap-1"
                                        data-testid={`sync-stock-${s.prefix}`}
                                    >
                                        {syncing[s.id] === "stock" ? <Loader2 className="animate-spin" size={12} /> : <Package size={12} />}
                                        Stocks
                                    </button>
                                </div>
                                <div className="text-[10px] font-mono text-ink/40 mt-2">
                                    Auto: completo {s.cron_full_enabled ? `às ${String(s.cron_full_hour).padStart(2,"0")}:00` : "off"} · stocks {s.cron_stock_enabled ? `cada ${s.cron_stock_interval_hours}h` : "off"}
                                </div>
                            </div>
                        ) : (
                            <div className="mt-4 text-[11px] font-mono text-ink/40 border-t-2 border-ink pt-3">
                                Sem URL remoto. Edite para configurar sync automático.
                            </div>
                        )}
                        <button onClick={() => setImporting(s)} className="btn-secondary w-full mt-3" data-testid={`import-${s.prefix}`}>
                            <Upload size={14} /> Importar ficheiro CSV
                        </button>
                    </div>
                ))}
                {list.length === 0 && (
                    <div className="md:col-span-2 border-2 border-ink p-10 text-center font-mono text-ink/60">
                        Sem fornecedores. Adicione um para automatizar a gestão de catálogo.
                    </div>
                )}
            </div>

            {edit && <SupplierModal initial={edit} onClose={() => setEdit(null)} onSave={save} />}
            {importing && <ImportModal supplier={importing} onClose={() => setImporting(null)} />}
        </div>
    );
}

function ImportModal({ supplier, onClose }) {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [onlyStock, setOnlyStock] = useState(true);
    const [limit, setLimit] = useState(0);
    const [pricingMode, setPricingMode] = useState(supplier.pricing_mode || "street");
    const [pricingValue, setPricingValue] = useState(supplier.pricing_value ?? "");

    const mode = PRICING_MODES.find((m) => m.v === pricingMode);

    const run = async () => {
        if (!file) return toast.error("Seleccione um ficheiro CSV");
        setBusy(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const params = new URLSearchParams();
            params.set("only_in_stock", onlyStock);
            if (limit) params.set("limit", limit);
            params.set("pricing_mode", pricingMode);
            if (pricingValue !== "") params.set("pricing_value", pricingValue);
            const { data } = await api.post(
                `/admin/suppliers/${supplier.id}/import?${params.toString()}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 },
            );
            setResult(data);
            toast.success(`Importado: ${data.created} criados, ${data.updated} actualizados`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro na importação");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border-2 border-ink w-full max-w-2xl shadow-[8px_8px_0_0_#0A0A0A] my-10">
                <div className="p-5 border-b-2 border-ink flex justify-between items-center">
                    <div>
                        <div className="label-mono text-magenta">Importar de</div>
                        <h2 className="font-display font-black text-2xl">{supplier.name} <span className="text-magenta">({supplier.prefix})</span></h2>
                    </div>
                    <button onClick={onClose}><X size={20} /></button>
                </div>
                <div className="p-6 space-y-5">
                    <div className="border-2 border-ink p-4 bg-paper">
                        <div className="label-mono mb-2">Como é gerado o SKU</div>
                        <div className="font-mono text-sm">
                            <span className="text-magenta font-bold">{supplier.prefix}</span>
                            {" + "}<span className="bg-paper border border-ink px-1">marca (2L)</span>
                            {" + "}<span className="bg-paper border border-ink px-1">TN/TT</span>
                            {" + "}<span className="bg-paper border border-ink px-1">O/C</span>
                            {" + "}<span className="bg-paper border border-ink px-1">ref. limpa</span>
                        </div>
                        <div className="text-xs text-ink/60 mt-2 font-mono">
                            Ex: TN2320 (Brother, compatível) → <strong className="text-magenta">{supplier.prefix}BRTNCTN2320</strong> · HPQ2612A → <strong className="text-magenta">{supplier.prefix}HPTNC2612A</strong> (HP removido do code)
                        </div>
                    </div>

                    <div>
                        <input ref={fileRef} type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} className="hidden" data-testid="csv-file-input" />
                        <button onClick={() => fileRef.current?.click()} className="btn-secondary w-full">
                            <Upload size={14} /> {file ? file.name : "Seleccionar CSV"}
                        </button>
                        {file && <div className="text-xs font-mono text-ink/60 mt-1">{(file.size / 1024).toFixed(0)} KB</div>}
                    </div>

                    <div>
                        <span className="label-mono mb-2 block">Estratégia de preço</span>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <select
                                value={pricingMode}
                                onChange={(e) => setPricingMode(e.target.value)}
                                className="input-edge"
                                data-testid="pricing-mode-select"
                            >
                                {PRICING_MODES.map((m) => (
                                    <option key={m.v} value={m.v}>{m.l}</option>
                                ))}
                            </select>
                            {mode?.needs && (
                                <input
                                    type="number"
                                    step="0.01"
                                    value={pricingValue}
                                    onChange={(e) => setPricingValue(e.target.value)}
                                    placeholder={`Valor em ${mode.needs}`}
                                    className="input-edge"
                                    data-testid="pricing-value-input"
                                />
                            )}
                        </div>
                        <div className="text-xs font-mono text-ink/60 mt-2">
                            {pricingMode === "street" && "→ Usa Street_price (PVR) do CSV directamente."}
                            {pricingMode === "cost_markup" && "→ Custo (Price) × (1 + valor%/100)"}
                            {pricingMode === "street_plus_pct" && "→ Street × (1 + valor%/100). Útil para acrescentar margem."}
                            {pricingMode === "street_plus_eur" && "→ Street + valor€ fixo. Útil para portes/comissão."}
                            {pricingMode === "manual" && "→ Todos os produtos ficam com este valor (raro)."}
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={onlyStock} onChange={(e) => setOnlyStock(e.target.checked)} className="accent-magenta w-4 h-4" />
                            <span className="text-sm font-bold uppercase tracking-wider">Apenas em stock</span>
                        </label>
                        <label className="block">
                            <span className="label-mono mb-1 block">Limite (0 = todos)</span>
                            <input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} className="input-edge" data-testid="import-limit" />
                        </label>
                    </div>

                    {result && !result.dry_run && (
                        <div className="border-2 border-accent-green bg-accent-green/10 p-4">
                            <div className="label-mono text-accent-green">Resultado</div>
                            <div className="grid grid-cols-3 gap-4 mt-2 font-mono">
                                <div><div className="text-xs text-ink/60">Criados</div><div className="font-display font-black text-2xl text-accent-green">{result.created}</div></div>
                                <div><div className="text-xs text-ink/60">Actualizados</div><div className="font-display font-black text-2xl">{result.updated}</div></div>
                                <div><div className="text-xs text-ink/60">Ignorados</div><div className="font-display font-black text-2xl text-ink/40">{result.skipped}</div></div>
                            </div>
                            {result.errors?.length > 0 && (
                                <div className="mt-3 text-xs">
                                    <div className="font-bold">{result.errors.length} erros (primeiros):</div>
                                    {result.errors.slice(0, 5).map((e, i) => (
                                        <div key={i} className="font-mono text-ink/60">linha {e.row}: {e.error}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {result?.dry_run && result.preview?.length > 0 && (
                        <div className="border-2 border-magenta bg-magenta/5 p-4 max-h-[420px] overflow-y-auto">
                            <div className="label-mono text-magenta mb-3">
                                Pré-visualização — assim ficariam os primeiros {result.preview.length} produtos:
                            </div>
                            <div className="space-y-3">
                                {result.preview.map((p, i) => (
                                    <div key={i} className="border-2 border-ink bg-white p-3 text-sm" data-testid={`preview-${p.sku}`}>
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <span className="font-mono font-bold text-magenta">{p.sku}</span>
                                            <span className="font-mono text-xs text-ink/50">code original: {p.raw_code}</span>
                                        </div>
                                        <div className="font-bold mt-1">{p.name}</div>
                                        <div className="font-mono text-xs text-ink/60 mt-1 grid sm:grid-cols-3 gap-1">
                                            <span>marca: {p.brand}</span>
                                            <span>cor: {p.color}</span>
                                            <span>páginas: {p.pages || "—"}</span>
                                            <span>preço: {p.price}€</span>
                                            <span>stock: {p.stock}</span>
                                            <span>cat: {p.category_slug}</span>
                                        </div>
                                        {p.compatible_printers?.length > 0 && (
                                            <div className="mt-2 text-[10px] font-mono text-ink/60">
                                                Compat ({p.compatible_printers.length}+): {p.compatible_printers.slice(0, 4).join(" · ")}…
                                            </div>
                                        )}
                                        {p.would_update_existing && (
                                            <div className="mt-1 text-[10px] font-mono text-magenta">⚠ já existe — seria actualizado</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3 text-xs font-mono text-ink/60">
                                Se algum campo está errado, edite a configuração do fornecedor (tabs <strong>Colunas CSV</strong> e <strong>Códigos de marca</strong>) antes do import real.
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t-2 border-ink">
                        <button onClick={onClose} className="btn-secondary" disabled={busy}>{result && !result.dry_run ? "Fechar" : "Cancelar"}</button>
                        <button onClick={() => run(true)} disabled={busy || !file} className="btn-secondary disabled:opacity-50" data-testid="preview-import-btn">
                            {busy && <Loader2 className="animate-spin" size={14} />}
                            Pré-visualizar
                        </button>
                        <button onClick={() => run(false)} disabled={busy || !file} className="btn-primary disabled:opacity-50" data-testid="run-import-btn">
                            {busy && <Loader2 className="animate-spin" size={14} />}
                            {busy ? "A processar…" : "Iniciar Importação"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SupplierModal({ initial, onClose, onSave }) {
    const [f, setF] = useState(initial);
    const [tab, setTab] = useState("main");
    const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
    const updMap = (group, key) => (e) =>
        setF((s) => ({ ...s, [group]: { ...s[group], [key]: e.target.value } }));

    return (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border-2 border-ink w-full max-w-2xl shadow-[8px_8px_0_0_#0A0A0A] my-10">
                <div className="p-5 border-b-2 border-ink flex justify-between">
                    <h2 className="font-display font-black text-2xl">
                        {f.id ? "Editar fornecedor" : "Novo fornecedor"}
                    </h2>
                    <button onClick={onClose}><X size={20} /></button>
                </div>
                <div className="border-b-2 border-ink flex">
                    {[
                        { k: "main", l: "Principal" },
                        { k: "columns", l: "Colunas CSV" },
                        { k: "brands", l: "Códigos de marca" },
                    ].map((t) => (
                        <button
                            key={t.k}
                            onClick={() => setTab(t.k)}
                            className={`px-5 py-3 text-sm font-bold uppercase tracking-wider border-r-2 last:border-r-0 border-ink ${
                                tab === t.k ? "bg-magenta text-white" : "hover:bg-paper"
                            }`}
                        >
                            {t.l}
                        </button>
                    ))}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="p-6 space-y-4">
                    {tab === "main" && (
                        <>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Nome *" v={f.name} on={upd("name")} required />
                                <Field label="Prefixo SKU * (ex: WL)" v={f.prefix} on={upd("prefix")} required />
                            </div>
                            <Field label="Website" v={f.website} on={upd("website")} />
                            <TextArea label="Notas" v={f.notes} on={upd("notes")} />
                            <div className="border-t-2 border-ink pt-4">
                                <div className="label-mono text-magenta mb-3">Sincronização remota</div>
                                <Field label="URL CSV remoto (use {idcat} como placeholder)" v={f.csv_url || ""} on={upd("csv_url")} />
                                <p className="text-[11px] font-mono text-ink/50 mt-1">
                                    Ex Life365: https://pt2.life365.eu/api/utils/csvdata/prodlist?l=conta&p=password&idcat={"{idcat}"}
                                </p>
                                <div className="grid sm:grid-cols-3 gap-3 mt-3">
                                    <Field label="idcat" v={f.csv_idcat || "1"} on={upd("csv_idcat")} />
                                    <Field label="Hora cron completo (0-23)" type="number" v={f.cron_full_hour ?? 3} on={upd("cron_full_hour")} />
                                    <Field label="Intervalo stock (horas)" type="number" v={f.cron_stock_interval_hours ?? 3} on={upd("cron_stock_interval_hours")} />
                                </div>
                                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                                    <label className="flex items-center gap-2">
                                        <input type="checkbox" checked={f.cron_full_enabled !== false} onChange={upd("cron_full_enabled")} className="accent-magenta w-4 h-4" />
                                        <span className="text-sm font-bold uppercase">Cron completo (1×/dia)</span>
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input type="checkbox" checked={f.cron_stock_enabled !== false} onChange={upd("cron_stock_enabled")} className="accent-magenta w-4 h-4" />
                                        <span className="text-sm font-bold uppercase">Cron de stocks (intervalo)</span>
                                    </label>
                                </div>
                                <p className="text-[11px] font-mono text-ink/50 mt-3">
                                    Para activar/alterar cron, reinicie o backend ou edite e guarde aqui (a alteração é aplicada no próximo arranque).
                                </p>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4 border-t-2 border-ink pt-4">
                                <label className="block">
                                    <span className="label-mono mb-2 block">Estratégia de preço default</span>
                                    <select value={f.pricing_mode || "street"} onChange={upd("pricing_mode")} className="input-edge">
                                        {PRICING_MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                                    </select>
                                </label>
                                <Field label="Valor (% ou €)" type="number" step="0.01" v={f.pricing_value} on={upd("pricing_value")} />
                            </div>
                            <Field label="Delimitador CSV" v={f.delimiter} on={upd("delimiter")} />
                            <div className="grid sm:grid-cols-3 gap-4 border-t-2 border-ink pt-4">
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={f.strip_code_prefix !== false} onChange={upd("strip_code_prefix")} className="accent-magenta w-4 h-4" />
                                    <span className="text-sm font-bold uppercase">Remover prefixo da marca do código</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={f.translate_color !== false} onChange={upd("translate_color")} className="accent-magenta w-4 h-4" />
                                    <span className="text-sm font-bold uppercase">Traduzir cor para PT</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={f.generate_pt_description !== false} onChange={upd("generate_pt_description")} className="accent-magenta w-4 h-4" />
                                    <span className="text-sm font-bold uppercase">Gerar descrição em PT</span>
                                </label>
                            </div>
                        </>
                    )}
                    {tab === "columns" && (
                        <div>
                            <p className="text-xs text-ink/60 mb-3">Mapeamento dos nomes das colunas no CSV do fornecedor.</p>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {Object.entries(f.column_map || {}).map(([k, v]) => (
                                    <Field key={k} label={k} v={v} on={updMap("column_map", k)} />
                                ))}
                            </div>
                        </div>
                    )}
                    {tab === "brands" && (
                        <div>
                            <p className="text-xs text-ink/60 mb-3">Mapeamento marca → código 2 letras para gerar o SKU.</p>
                            <div className="grid sm:grid-cols-3 gap-2">
                                {Object.entries(f.brand_codes || {}).map(([k, v]) => (
                                    <div key={k} className="flex items-center gap-2">
                                        <span className="label-mono w-24 truncate">{k}</span>
                                        <input value={v} maxLength={3} onChange={updMap("brand_codes", k)} className="input-edge w-16 text-center font-mono uppercase" />
                                    </div>
                                ))}
                            </div>
                            <p className="mt-3 text-xs font-mono text-ink/60">
                                Pode editar via API se precisar adicionar marcas adicionais.
                            </p>
                        </div>
                    )}
                    <div className="flex justify-end gap-3 pt-4 border-t-2 border-ink">
                        <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                        <button type="submit" className="btn-primary">Guardar</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, v, on, type = "text", step, required }) {
    return (
        <label className="block">
            <span className="label-mono mb-2 block">{label}</span>
            <input type={type} step={step} value={v ?? ""} onChange={on} required={required} className="input-edge" />
        </label>
    );
}
function TextArea({ label, v, on }) {
    return (
        <label className="block">
            <span className="label-mono mb-2 block">{label}</span>
            <textarea value={v ?? ""} onChange={on} rows={2} className="input-edge" />
        </label>
    );
}
